import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import ProductGrid from '../components/ProductGrid';
import AuthPanel from '../components/AuthPanel';
import TrackingBanner from '../components/TrackingBanner';
import ProfileSetup from '../components/ProfileSetup';
import { useAppContext } from '../context/AppContext';
import { ROUTES } from '../navigation';
import { useMobileBack, OVERLAY_PRIORITY } from '../navigation';

const categoryTiles = [
  { label: 'All', icon: '🛍️', color: '#f3f4f6' },
  { label: 'Offers', icon: '🏷️', color: '#fef9c3' },
  { label: 'Grocery', icon: '🧺', color: '#fff3e0' },
  { label: 'Snacks', icon: '🍪', color: '#fce4ec' },
  { label: 'Dairy', icon: '🥛', color: '#e3f2fd' },
  { label: 'Vegetables', icon: '🥦', color: '#e8f5e9' },
  { label: 'Fruits', icon: '🍎', color: '#ffebee' },
  { label: 'Household', icon: '🧼', color: '#e0f2f1' },
];

function HomePage() {
  const navigate = useNavigate();
  const {
    products,
    currentUser,
    cart,
    userName,
    needsProfileSetup,
    setNeedsProfileSetup,
    cartQty,
    handleAuthReady,
    handleAddToCart,
    handleUpdateQuantity,
    handleLogout,
    setTrackingOrderId,
    headerRef,
  } = useAppContext();

  const [searchText, setSearchText] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [zoomedImage, setZoomedImage] = useState(null);

  // Mobile back button for image zoom
  useMobileBack({
    id: 'image-zoom',
    priority: OVERLAY_PRIORITY.IMAGE_ZOOM,
    isOpen: zoomedImage !== null,
    onClose: useCallback(() => setZoomedImage(null), []),
  });

  const filteredItems = useMemo(() => {
    return products.filter((item) => {
      if (activeCategory === 'Offers') {
        return item.originalPrice && item.originalPrice > item.price;
      }
      const matchesCategory = activeCategory === 'All' || item.category === activeCategory;
      const matchesSearch = item.name.toLowerCase().includes(searchText.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [products, activeCategory, searchText]);

  const filteredBrandCount = useMemo(() => {
    return new Set(
      filteredItems
        .map((item) => item.brand)
        .filter(Boolean)
    ).size;
  }, [filteredItems]);

  if (needsProfileSetup && currentUser) {
    return <ProfileSetup user={currentUser} onComplete={() => setNeedsProfileSetup(false)} />;
  }

  return (
    <>
      {/* Full-width header above the sidebar/content split */}
      <div ref={headerRef} className="page-main-header">
        <Header
          searchText={searchText}
          onSearchChange={setSearchText}
          isSignedIn={Boolean(currentUser)}
          onOpenAccount={() => navigate(ROUTES.ACCOUNT)}
          onOpenOrders={() => navigate(ROUTES.ORDERS)}
          onOpenAddresses={() => navigate(ROUTES.ADDRESSES)}
          cartCount={cartQty}
          onOpenCart={() => navigate(ROUTES.CART)}
          showSearch={true}
          onLogoClick={null}
          onLogout={handleLogout}
          userName={userName}
          userPhone={currentUser?.phoneNumber}
          onHelp={() => navigate(ROUTES.HELP)}
        />
      </div>

      {/* Sidebar + product listing below header */}
      <div className="page-body">
        <aside className="category-sidebar">
          {categoryTiles.map((category) => {
            const isActive = activeCategory === category.label;
            return (
              <button
                key={category.label}
                type="button"
                className={`category-sidebar-btn ${isActive ? 'active' : ''}`}
                style={{ background: isActive ? category.color : 'transparent' }}
                onClick={() => { setActiveCategory(category.label); setSearchText(''); }}
              >
                <span className="cat-icon">{category.icon}</span>
                <span className="cat-label">{category.label}</span>
              </button>
            );
          })}
        </aside>

        <div className="page-main">
          {/* Auth banner */}
          {!currentUser && (
            <div className="auth-inline-section">
              <AuthPanel onAuthReady={handleAuthReady} />
            </div>
          )}

          {/* Tracking banner */}
          {!searchText && currentUser && (
            <TrackingBanner user={currentUser} hasCart={cart.length > 0} onViewTracking={(orderId) => {
              setTrackingOrderId(orderId);
              navigate(`/account/orders/${orderId}`);
            }} />
          )}

          <div className="results">
            <p>Showing {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''}</p>
            <p className="muted">{filteredBrandCount} brand{filteredBrandCount !== 1 ? 's' : ''} in results.</p>
          </div>
          {filteredItems.length === 0 && searchText ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
              <p style={{ fontSize: '18px', marginBottom: '8px' }}>Not found</p>
              <p style={{ fontSize: '14px' }}>We are working on bringing this to you soon</p>
            </div>
          ) : (
            <ProductGrid
              items={filteredItems}
              onAddToCart={handleAddToCart}
              onUpdateQuantity={handleUpdateQuantity}
              cart={cart}
              onImageClick={(product) => setZoomedImage(product)}
            />
          )}
        </div>
      </div>

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '40px 20px',
            cursor: 'zoom-out'
          }}
          onClick={() => setZoomedImage(null)}
        >
          <div style={{ 
            maxWidth: '500px',
            width: '100%',
            background: 'white',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
          }}>
            {zoomedImage.image ? (
              <img 
                src={zoomedImage.image} 
                alt={zoomedImage.name} 
                style={{ 
                  width: '100%',
                  height: 'auto',
                  display: 'block'
                }} 
              />
            ) : (
              <div style={{ fontSize: '120px', padding: '60px', textAlign: 'center', background: '#f5f5f5' }}>📦</div>
            )}
            <div style={{ padding: '16px' }}>
              <h3 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: '600' }}>
                {zoomedImage.brand ? `${zoomedImage.brand} ${zoomedImage.name}` : zoomedImage.name}
              </h3>
              <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#666' }}>{zoomedImage.unit}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '20px', fontWeight: '700', color: 'var(--primary)' }}>₹{zoomedImage.price}</span>
                {zoomedImage.originalPrice && (
                  <>
                    <span style={{ fontSize: '15px', color: '#999', textDecoration: 'line-through' }}>₹{zoomedImage.originalPrice}</span>
                    <span style={{ 
                      background: 'var(--success-bg)', 
                      color: 'var(--success)', 
                      padding: '3px 6px', 
                      borderRadius: '4px', 
                      fontSize: '11px', 
                      fontWeight: '600' 
                    }}>
                      {Math.round(((zoomedImage.originalPrice - zoomedImage.price) / zoomedImage.originalPrice) * 100)}% OFF
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default HomePage;
